import type { ComponentInit } from "@scripts/registry";

const VIEWPORT = "[data-fa-logo-ticker-viewport]";
const TRACK = "[data-fa-logo-ticker-track]";

const READY = "fa-logo-ticker--ready";
const DEFAULT_SPEED = 20;
const RESIZE_DEBOUNCE = 150;
const SETTLE_DELAY = 1500;

export const initLogoTicker: ComponentInit = (root) => {
	const viewport = root.querySelector<HTMLElement>(VIEWPORT);
	const track = root.querySelector<HTMLElement>(TRACK);

	if (!viewport || !track) return;

	const authored = Array.from(track.children).filter(
		(child): child is HTMLElement => child instanceof HTMLElement,
	);

	if (authored.length === 0) return;

	const controller = new AbortController();
	const { signal } = controller;
	const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");

	let clones: HTMLElement[] = [];
	let resizeTimer = 0;
	let settleTimer = 0;
	let frame = 0;
	let lastFrame = 0;
	let row = 0;
	let position = 0;
	let perSecond = DEFAULT_SPEED;
	let applied = "";
	let hovered = false;
	let settling = false;
	let onScreen = false;

	/* Summed rather than measured off the track: the cells are fixed-width and the column gap is
	   zero, so this is exact without a reflow. */
	const measureRow = () =>
		authored.reduce((total, item) => total + item.getBoundingClientRect().width, 0);

	const signature = (width: number, measured: number) =>
		`${Math.round(width)}:${Math.round(measured)}`;

	const speed = () => {
		const declared = Number.parseFloat(
			getComputedStyle(root).getPropertyValue("--fa-logo-ticker-speed"),
		);

		return Number.isFinite(declared) && declared > 0 ? declared : DEFAULT_SPEED;
	};

	const focused = () => viewport.contains(document.activeElement);

	const stop = () => {
		if (frame !== 0) cancelAnimationFrame(frame);
		frame = 0;
		lastFrame = 0;
	};

	/* Suspended while focus is inside: a wrap moves the focused logo a row width under the
	   reader, and the row it would land in is aria-hidden clones. */
	const wrap = () => {
		if (row === 0 || focused()) return;

		const x = viewport.scrollLeft;

		if (x < row) position = x + row;
		else if (x > row * 2) position = x - row;
		else return;

		viewport.scrollLeft = position;
	};

	/* Re-read when the gap opens: a user scroll writes scrollLeft behind the accumulator, and
	   an engine that rounds the assignment would otherwise never advance at 20px per second. */
	const tick = (now: number) => {
		frame = requestAnimationFrame(tick);

		const elapsed = lastFrame === 0 ? 0 : (now - lastFrame) / 1000;

		lastFrame = now;

		if (elapsed === 0) return;

		if (Math.abs(viewport.scrollLeft - position) > 1) position = viewport.scrollLeft;

		position += perSecond * elapsed;
		viewport.scrollLeft = position;
		wrap();
	};

	const sync = () => {
		if (row === 0 || !onScreen || hovered || settling || focused() || reduced.matches) {
			stop();
			return;
		}

		if (frame === 0) frame = requestAnimationFrame(tick);
	};

	const interact = () => {
		settling = true;
		stop();
		window.clearTimeout(settleTimer);
		settleTimer = window.setTimeout(() => {
			settling = false;
			sync();
		}, SETTLE_DELAY);
	};

	const removeClones = () => {
		for (const clone of clones) clone.remove();
		clones = [];
	};

	const reset = () => {
		stop();
		removeClones();
		root.classList.remove(READY);
		viewport.scrollLeft = 0;
		position = 0;
		row = 0;
	};

	const cloneRow = () =>
		authored.map((item) => {
			const clone = item.cloneNode(true) as HTMLElement;

			clone.setAttribute("aria-hidden", "true");

			/* A clone is off-screen for most of its life, so focus must never land in one. */
			for (const link of clone.querySelectorAll("a")) link.setAttribute("tabindex", "-1");

			clones.push(clone);

			return clone;
		});

	const layout = () => {
		reset();

		if (reduced.matches) return;

		const measured = measureRow();
		const width = viewport.clientWidth;

		applied = signature(width, measured);

		/* Zero while an ancestor is `display: none`, and a row no wider than the viewport has
		   nothing to advance past — both leave the authored row centred and scrollless. */
		if (measured === 0 || measured <= width) return;

		row = measured;
		perSecond = speed();

		track.prepend(...cloneRow());

		const copies = Math.ceil(width / row) + 1;

		for (let copy = 0; copy < copies; copy += 1) track.append(...cloneRow());

		position = row;
		viewport.scrollLeft = row;
		root.classList.add(READY);
		sync();
	};

	/* Driven by the values the layout derives from rather than by the event: a mobile browser
	   fires `resize` on every scroll, where only the height has moved. */
	const relayout = () => {
		if (signature(viewport.clientWidth, measureRow()) !== applied) layout();
	};

	layout();

	const observer = new ResizeObserver(() => {
		window.clearTimeout(resizeTimer);
		resizeTimer = window.setTimeout(relayout, RESIZE_DEBOUNCE);
	});

	observer.observe(viewport);
	for (const item of authored.slice(0, 1)) observer.observe(item);

	const visibility = new IntersectionObserver((records) => {
		for (const record of records) onScreen = record.isIntersecting;
		sync();
	});

	visibility.observe(viewport);

	reduced.addEventListener("change", layout, { signal });

	/* Mouse only: a touch `pointerenter` has no matching leave, so a tap would pause for good. */
	viewport.addEventListener(
		"pointerenter",
		(event) => {
			if (event.pointerType !== "mouse") return;

			hovered = true;
			sync();
		},
		{ signal },
	);

	viewport.addEventListener(
		"pointerleave",
		(event) => {
			if (event.pointerType !== "mouse") return;

			hovered = false;
			sync();
		},
		{ signal },
	);

	viewport.addEventListener("pointerdown", interact, { signal });
	viewport.addEventListener("wheel", interact, { passive: true, signal });
	viewport.addEventListener("touchstart", interact, { passive: true, signal });

	/* The settle timer restarts off momentum rather than `scrollend`, which Safari shipped late
	   and nothing else here depends on. */
	viewport.addEventListener(
		"scroll",
		() => {
			if (settling) interact();
			else wrap();
		},
		{ signal },
	);

	viewport.addEventListener("focusin", sync, { signal });
	viewport.addEventListener("focusout", () => window.setTimeout(sync), { signal });

	return () => {
		window.clearTimeout(resizeTimer);
		window.clearTimeout(settleTimer);
		observer.disconnect();
		visibility.disconnect();
		controller.abort();
		reset();
	};
};
